import GppGoodRounded from '@mui/icons-material/GppGoodRounded';
import LockClockRounded from '@mui/icons-material/LockClockRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { fonts } from '../design/theme';
import { BrandMark, ModeToggle } from '../layout/parts';
import { PoweredBy } from '../components/PoweredBy';

const glass = {
  bgcolor: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.14)',
  backdropFilter: 'blur(18px) saturate(140%)',
  boxShadow: '0 30px 60px -24px rgba(5,4,30,0.7), inset 0 1px 0 rgba(255,255,255,0.08)',
  borderRadius: '18px',
  color: '#fff',
} as const;

/** Split frame for sign-in and MFA screens: a console showcase on the left, the form on the right. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  icon,
}: {
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Replaces the brand mark above the title (e.g. a shield on the MFA step). */
  icon?: ReactNode;
}) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '1fr 1fr', xl: '1.1fr 1fr' },
        bgcolor: 'background.default',
      }}
    >
      <Showcase />

      <Box
        sx={(t) => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, sm: 4 },
          backgroundImage: 'radial-gradient(rgba(49,46,129,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          ...t.applyStyles('dark', {
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
          }),
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ alignItems: 'center', display: { md: 'none' } }}
          >
            <BrandMark size={34} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                PackPilot
              </Typography>
              <Typography variant="caption" color="primary" sx={{ fontWeight: 650 }}>
                Platform console
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ ml: 'auto' }}>
            <ModeToggle />
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', py: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              p: { xs: 0, sm: 5 },
              borderRadius: '24px',
              bgcolor: { sm: 'background.paper' },
              border: { sm: 1 },
              borderColor: { sm: 'divider' },
              boxShadow: {
                sm: '0 1px 2px rgba(16,24,40,0.04), 0 24px 48px -16px rgba(30,27,75,0.16)',
              },
              animation: 'sm-rise 420ms cubic-bezier(0.2,0,0,1)',
              '@keyframes sm-rise': {
                from: { opacity: 0, transform: 'translateY(12px)' },
                to: { opacity: 1, transform: 'none' },
              },
            }}
          >
            <Box sx={{ display: { xs: icon ? 'block' : 'none', md: 'block' }, mb: 3 }}>
              {icon ?? <BrandMark size={44} />}
            </Box>
            <Typography
              variant="h2"
              component="h1"
              sx={{ fontSize: { xs: '1.75rem', sm: '1.875rem' }, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3.5 }}>
              {subtitle}
            </Typography>
            {children}
            {footer && <Box sx={{ mt: 3 }}>{footer}</Box>}
          </Box>
        </Box>

        <Stack spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Restricted system · Authorised platform staff only · Every action is audited
          </Typography>
          <PoweredBy />
        </Stack>
      </Box>
    </Box>
  );
}

const BARS = [34, 48, 40, 62, 55, 70, 64, 82, 76, 90, 84, 100];

function Showcase() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'relative',
        overflow: 'hidden',
        flexDirection: 'column',
        p: { md: 5, lg: 7 },
        color: '#fff',
        background: 'linear-gradient(160deg, #06372F 0%, #04302A 45%, #021A16 100%)',
        isolation: 'isolate',
      }}
    >
      {/* Soft light pools and a faint grid */}
      <Box
        sx={{
          position: 'absolute',
          zIndex: -1,
          inset: 0,
          background:
            'radial-gradient(600px 420px at 15% 10%, rgba(52,168,135,0.42), transparent 70%), radial-gradient(520px 420px at 85% 75%, rgba(56,189,248,0.22), transparent 70%), radial-gradient(400px 300px at 70% 20%, rgba(16,185,129,0.18), transparent 70%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 40%, black 10%, transparent 72%)',
        }}
      />

      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <BrandMark size={40} />
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', lineHeight: 1.2 }}>
            PackPilot
          </Typography>
          <Typography variant="caption" sx={{ color: '#7FD8BE', fontWeight: 650 }}>
            Platform console
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: { md: 7, lg: 9 }, maxWidth: 520 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            mb: 2.5,
            borderRadius: 999,
            fontSize: 12.5,
            fontWeight: 600,
            bgcolor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.16)',
          }}
        >
          <GppGoodRounded sx={{ fontSize: 15, color: '#6EE7B7' }} />
          Tenants · Subscriptions · Codes · Audit
        </Box>
        <Typography
          variant="h1"
          sx={{
            color: '#fff',
            fontSize: { md: '2.5rem', lg: '3rem' },
            lineHeight: 1.08,
            letterSpacing: '-0.035em',
          }}
        >
          Every shop on the platform,{' '}
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(90deg, #B7EBDC, #7FD8BE 45%, #7DD3FC)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            at a glance
          </Box>
          .
        </Typography>
        <Typography
          sx={{ mt: 2, color: 'rgba(224,231,255,0.72)', fontSize: '1.0625rem', maxWidth: 440 }}
        >
          Onboard tenants, manage subscriptions and activation codes, and keep the platform healthy
          — with a full audit trail.
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', flex: 1, minHeight: 300, mt: 5 }}>
        <Box
          sx={{
            ...glass,
            position: 'absolute',
            left: 0,
            top: 40,
            width: { md: 290, lg: 330 },
            p: 2.5,
          }}
        >
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(224,231,255,0.8)' }}>
              Active tenants
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                bgcolor: 'rgba(110,231,183,0.14)',
                color: '#6EE7B7',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <NorthEastRounded sx={{ fontSize: 13 }} /> 12 this week
            </Box>
          </Stack>
          <Typography sx={{ fontFamily: fonts.display, fontSize: 30, fontWeight: 750, mt: 0.5 }}>
            1,284
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ alignItems: 'flex-end', height: 64, mt: 1.5 }}
          >
            {BARS.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: `${String(h)}%`,
                  borderRadius: '4px',
                  background:
                    i === BARS.length - 1
                      ? 'linear-gradient(180deg, #B7EBDC, #3FB394)'
                      : 'linear-gradient(180deg, rgba(183,235,220,0.55), rgba(63,179,148,0.2))',
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            ...glass,
            position: 'absolute',
            right: { md: 0, lg: 24 },
            top: 0,
            width: 260,
            p: 2,
            borderRadius: '16px',
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#05473E',
                boxShadow: '0 6px 14px -4px rgba(79,70,229,0.7)',
              }}
            >
              <LockClockRounded fontSize="small" />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Subscription extended
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(224,231,255,0.65)' }}>
                ahmed-hardware · +30 days
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            ...glass,
            position: 'absolute',
            right: { md: 10, lg: 50 },
            top: 190,
            width: 240,
            p: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(224,231,255,0.65)', fontWeight: 600 }}>
            System health
          </Typography>
          {[
            ['API', 'Operational'],
            ['Database', '4 ms'],
            ['Sync', '0 failed'],
          ].map(([k, v]) => (
            <Stack
              key={k}
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: '#34D399',
                    boxShadow: '0 0 0 3px rgba(52,211,153,0.2)',
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {k}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'rgba(224,231,255,0.7)' }}>
                {v}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
